// src/features/bootstrap/ExecutionPanel.tsx
// Execution progress, unseal-key acknowledgment, retry and reset.
// See docs/ux/bootstrap-ia.md section 2.

import { useEffect, useRef, useState } from 'react';
import { useI18n, useBootstrap } from '@react/hooks';
import { Button, ErrorAlert } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { BootstrapExecution, BootstrapStatus } from '@core/types/bootstrap';
import { executionStepVariant, enumSuffix } from './logic';

const POLL_MS = 2000;

export function ExecutionPanel({ status, onChanged }: { status?: BootstrapStatus; onChanged: () => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [execution, setExecution] = useState<BootstrapExecution | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isAcking, setIsAcking] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [verificationKey, setVerificationKey] = useState('');
  const [keysRevealed, setKeysRevealed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const poll = async () => {
    try {
      const r = await store.GetExecutionProgress();
      setExecution(r.execution ?? null);
      if (r.status === 'BOOTSTRAP_STATUS_COMPLETED' || r.status === 'BOOTSTRAP_STATUS_FAILED') {
        stopPolling();
        onChanged();
      }
    } catch {
      // handled by store.error
    }
  };

  useEffect(() => {
    if (status === 'BOOTSTRAP_STATUS_EXECUTING') {
      poll();
      timerRef.current = setInterval(poll, POLL_MS);
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleExecute = async () => {
    setIsExecuting(true);
    try {
      const r = await store.Execute({});
      setExecution(r.execution ?? null);
      onChanged();
      timerRef.current = setInterval(poll, POLL_MS);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleAck = async () => {
    if (!execution) return;
    setIsAcking(true);
    try {
      await store.AcknowledgeVaultKeys({ execution_id: execution.execution_id, verification_key: verificationKey || undefined });
      setVerificationKey('');
      poll();
    } finally {
      setIsAcking(false);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      const r = await store.RetryExecution({});
      setExecution(r.execution ?? null);
      onChanged();
      timerRef.current = setInterval(poll, POLL_MS);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      await store.Reset({});
      setShowReset(false);
      setExecution(null);
      onChanged();
    } finally {
      setIsResetting(false);
    }
  };

  const awaitingVaultAck = Boolean(execution?.vault_init_result);

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">{t('bootstrap.execution.title')}</h2>
        {status !== 'BOOTSTRAP_STATUS_COMPLETED' && (
          <div className="flex items-center gap-2">
            {status === 'BOOTSTRAP_STATUS_FAILED' && (
              <Button size="sm" variant="secondary" onClick={handleRetry} disabled={isRetrying}>
                {isRetrying ? t('common.loading') : t('bootstrap.execution.retry')}
              </Button>
            )}
            {status !== 'BOOTSTRAP_STATUS_EXECUTING' && (
              <Button size="sm" onClick={handleExecute} disabled={isExecuting}>
                {isExecuting ? t('common.loading') : t('bootstrap.execution.execute')}
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setShowReset(true)}>
              {t('bootstrap.execution.reset')}
            </Button>
          </div>
        )}
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {execution && (
        <>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary transition-all" style={{ width: `${execution.progress_percent}%` }} />
          </div>
          {execution.error_message && (
            <p className="text-sm text-destructive">{execution.error_message}</p>
          )}
          <ul className="divide-y divide-border rounded-md border border-border">
            {execution.steps.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-3 py-1.5">
                <div>
                  <p className="text-sm text-foreground">{s.name}</p>
                  {s.error_message && <p className="text-xs text-destructive">{s.error_message}</p>}
                </div>
                <StatusBadge variant={executionStepVariant(s.status)} label={t(`bootstrap.execution.stepStatus.${enumSuffix(s.status, 'BOOTSTRAP_STEP_STATUS_')}`)} size="sm" />
              </li>
            ))}
          </ul>

          {awaitingVaultAck && execution.vault_init_result && (
            <div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-3">
              <p className="text-sm font-medium text-foreground">{t('bootstrap.execution.vaultKeysTitle')}</p>
              <p className="text-xs text-muted-foreground">{t('bootstrap.execution.vaultKeysWarning')}</p>
              {!keysRevealed ? (
                <Button size="sm" variant="secondary" onClick={() => setKeysRevealed(true)}>{t('bootstrap.execution.revealKeys')}</Button>
              ) : (
                <ul className="space-y-1 font-mono text-xs">
                  {execution.vault_init_result.unseal_keys_b64.map((k, i) => (
                    <li key={i} className="break-all rounded bg-muted/50 px-2 py-1 select-all">{k}</li>
                  ))}
                  <li className="break-all rounded bg-muted/50 px-2 py-1 select-all">{t('bootstrap.execution.rootToken')}: {execution.vault_init_result.root_token}</li>
                </ul>
              )}
              {keysRevealed && (
                <div className="flex items-end gap-2">
                  <input
                    type="text"
                    placeholder={t('bootstrap.execution.verifyKeyPlaceholder')}
                    value={verificationKey}
                    onChange={(e) => setVerificationKey(e.target.value)}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  />
                  <Button size="sm" onClick={handleAck} disabled={isAcking}>
                    {isAcking ? t('common.loading') : t('bootstrap.execution.ackKeys')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!execution && <p className="text-sm text-muted-foreground">{t('bootstrap.execution.empty')}</p>}

      <ConfirmDialog
        open={showReset}
        title={t('bootstrap.execution.resetConfirmTitle')}
        description={t('bootstrap.execution.resetConfirmDescription')}
        destructive
        isConfirming={isResetting}
        onConfirm={handleReset}
        onCancel={() => setShowReset(false)}
      />
    </div>
  );
}
